import React, { useCallback, useState, useMemo, useEffect } from "react";
import TextMoney from "../../components/TextMoney";
import useCartStore from "../../zustands/useCartStore";
import Product from "../../constants/Product";
import { getApiData, getApiErrors } from "../../../frontend/api/http";
import { resolveProductDisplayPreset } from "../../utils/productDisplayPreset";

/**
 * Types matching the API response format from cp-promotion /site/flashsales
 */
type FlashSaleDetailItem = {
    id: number;
    product_id: number;
    product_variant_id: number;
    flash_sale_id: number;
    pricing_mode: number;
    uom_unit_price: number;
    min_uom_quantity: number;
    max_uom_quantity: number;
    max_per_customer: number;
    quota_total_quantity: number;
    quota_used: number;
    description: string;
    display_order: number;
    status: number;
};

type FlashSaleData = {
    id: number;
    name: string;
    title: string;
    description: string;
    seo_url?: string;
    date_started: number;
    date_ended: number;
    details: FlashSaleDetailItem[];
};

type ApiPriceByUom = { uom_id: number; value: number; min_quantity?: number };
type ApiPriceByPolicy = {
    price_policy_id: number;
    price_by_uom: ApiPriceByUom[];
};

type ApiVariant = {
    id: number;
    sku: string;
    displayname: string;
    baseprice: number;
    sellprice: number;
    compareprice: number;
    discountpercent: number;
    optionvalues: any[];
    pricebypolicy: ApiPriceByPolicy[];
    pricebyuom: ApiPriceByUom[];
    sellpriceforuoms: any[];
    photogalleryfilelist: any[];
};

type ApiPromotionEngine = {
    product_id?: number;
    product_variant_id?: number;
    unit_price?: number;
    unit_price_original?: number;
    product_promotion_decision?: {
        applied?: { id: number; type: string; value: number }[];
        eligible?: { id: number }[];
    };
};

type ApiProduct = {
    id: number;
    name: string;
    seourl: string;
    thumbnails: { url: string }[];
    variants: ApiVariant[];
    selectableoptions: any[];
    productuoms: { uom_id?: number; uomid?: number; uom_name?: string; uomname?: string; factor: number }[];
    weightsalemode: number;
    pricingtype: number;
    weightpresets?: any[];
    promotionengine: ApiPromotionEngine;
};

const PRICING_MODE_FINAL = 3;
const DETAIL_STATUS_ENABLE = 1;

// ─── Pricing helpers ───

function getVariantPriceByPolicy(v: ApiVariant, pricePolicyId: number): number {
    if (pricePolicyId > 0 && Array.isArray(v.pricebypolicy)) {
        for (const pp of v.pricebypolicy) {
            if (pp.price_policy_id === pricePolicyId) {
                for (const pu of pp.price_by_uom) {
                    if (pu.uom_id === 0) return pu.value;
                }
            }
        }
    }
    return v.baseprice || 0;
}

function getProductPrice(product: ApiProduct, variant: ApiVariant, pricePolicyId: number) {
    const priceBefore = variant.baseprice || 0;
    let priceSell = getVariantPriceByPolicy(variant, pricePolicyId);
    let finalPriceBefore = priceBefore;

    const pe = product.promotionengine;
    if (pe && typeof pe === "object" && !Array.isArray(pe)) {
        const decision = pe.product_promotion_decision;
        if (decision?.applied && decision.applied.length > 0) {
            if ((pe.unit_price || 0) > 0 && (pe.unit_price || 0) < (pe.unit_price_original || 0)) {
                priceSell = pe.unit_price!;
                finalPriceBefore = pe.unit_price_original!;
            }
        }
    }

    return { priceSell, priceBefore: finalPriceBefore };
}

// ─── Countdown Timer (plain text) ───

const CountdownTimer: React.FC<{ targetTimestamp: number; label: string }> = ({ targetTimestamp, label }) => {
    const [now, setNow] = useState(Math.floor(Date.now() / 1000));

    useEffect(() => {
        const timer = setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
        return () => clearInterval(timer);
    }, []);

    const diff = targetTimestamp - now;
    if (diff <= 0) return <span className="text-xs text-red-600 font-medium">Đã kết thúc</span>;

    const days = Math.floor(diff / 86400);
    const hours = Math.floor((diff % 86400) / 3600);
    const minutes = Math.floor((diff % 3600) / 60);
    const seconds = diff % 60;

    const parts: string[] = [];
    if (days > 0) parts.push(`${days} ngày`);
    parts.push(`${String(hours).padStart(2, "0")} giờ`);
    parts.push(`${String(minutes).padStart(2, "0")} phút`);
    parts.push(`${String(seconds).padStart(2, "0")} giây`);

    return (
        <span className="text-xs text-gray-500">
            {label} <span className="font-medium text-gray-600">{parts.join(", ")}</span>
        </span>
    );
};

// ─── Quota Progress Bar ───

const QuotaProgressBar: React.FC<{ used: number; total: number; isKgProduct: boolean }> = ({ used, total, isKgProduct }) => {
    const remaining = Math.max(0, total - used);
    const pct = total > 0 ? Math.min(100, (used / total) * 100) : 0;

    const formatQty = (v: number) => isKgProduct ? `${(v / 1000).toLocaleString("vi-VN")} kg` : v.toLocaleString("vi-VN");

    return (
        <div className="w-full">
            <div className="flex justify-between text-[10px] text-gray-500 mb-0.5">
                <span>📦 Còn {formatQty(remaining)}/{formatQty(total)}</span>
            </div>
            <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                    className="h-full rounded-full transition-all duration-300"
                    style={{ width: `${pct}%`, background: pct > 80 ? "#ef4444" : "var(--color-brand, #f97316)" }}
                />
            </div>
        </div>
    );
};

// ─── Flash Sale Block (one per flash sale) ───

const FlashSaleBlock: React.FC<{
    flashSale: FlashSaleData;
    product: ApiProduct;
    selectedVariantId: number | null;
    selectedPreset: any;
    quantity: number;
    pricePolicyId: number;
    allowBuyNow: boolean;
    setCart: (cart: any) => void;
}> = ({
    flashSale,
    product,
    selectedVariantId,
    selectedPreset,
    quantity,
    pricePolicyId,
    allowBuyNow,
    setCart,
}) => {
        const isKgProduct = useMemo(() => {
            return (
                product.weightsalemode === Product.WEIGHT_SALE_MODE_WEIGHT ||
                product.weightsalemode === Product.WEIGHT_SALE_MODE_COUNTING
            );
        }, [product.weightsalemode]);

        const isEnded = useMemo(() => {
            return flashSale.date_ended > 0 && Date.now() / 1000 > flashSale.date_ended;
        }, [flashSale.date_ended]);

        // Find matching detail for this product + variant
        // Priority: exact variant match > generic (pvid=0) match > any product match
        const matchingDetail = useMemo(() => {
            const enabledDetails = flashSale.details.filter(
                (d) => Number(d.product_id) === Number(product.id) && d.status === DETAIL_STATUS_ENABLE
            );
            if (enabledDetails.length === 0) return null;

            // 1. Exact variant match
            if (selectedVariantId && selectedVariantId > 0) {
                const variantDetail = enabledDetails.find(
                    (d) => Number(d.product_variant_id) === Number(selectedVariantId)
                );
                if (variantDetail) return variantDetail;
            }

            // 2. Generic detail (product_variant_id = 0, applies to all variants)
            const genericDetail = enabledDetails.find((d) => Number(d.product_variant_id) === 0);
            if (genericDetail) return genericDetail;

            // 3. If no variant selected yet, return first detail (for display purposes)
            if (!selectedVariantId || selectedVariantId <= 0) {
                return enabledDetails[0];
            }

            // 4. Selected variant has no specific detail and no generic detail
            // Still return first detail for block display, but price won't be overridden
            return enabledDetails[0];
        }, [flashSale.details, product.id, selectedVariantId]);

        // Check if the matching detail actually applies to the selected variant
        const detailAppliesToVariant = useMemo(() => {
            if (!matchingDetail || !selectedVariantId || selectedVariantId <= 0) return false;
            // Applies if: detail is for this exact variant OR detail is generic (pvid=0)
            return Number(matchingDetail.product_variant_id) === Number(selectedVariantId)
                || Number(matchingDetail.product_variant_id) === 0;
        }, [matchingDetail, selectedVariantId]);

        // Match variant
        const matchedVariant = useMemo(() => {
            if (!product.variants || product.variants.length === 0 || !matchingDetail) return null;

            if (selectedVariantId && selectedVariantId > 0) {
                const variant = product.variants.find((v) => Number(v.id) === Number(selectedVariantId));
                if (variant) return variant;
            }

            // If detail has product_variant_id, prefer that
            if (matchingDetail.product_variant_id > 0) {
                const variant = product.variants.find((v) => Number(v.id) === Number(matchingDetail.product_variant_id));
                if (variant) return variant;
            }

            return product.variants[0] || null;
        }, [product.variants, selectedVariantId, matchingDetail]);

        if (!matchingDetail || !matchedVariant) return null;

        // Check if product requires variant selection
        const requiresVariantSelection = useMemo(() => {
            return Array.isArray(product.selectableoptions) && product.selectableoptions.length > 0;
        }, [product.selectableoptions]);

        const isVariantSelected = !requiresVariantSelection || (selectedVariantId !== null && selectedVariantId > 0);

        const quotaTotal = matchingDetail.quota_total_quantity || 0;
        const quotaUsed = matchingDetail.quota_used || 0;
        const isQuotaExhausted = quotaTotal > 0 && quotaUsed >= quotaTotal;
        const isDisabled = isEnded || isQuotaExhausted || !isVariantSelected;

        const hasPreset = !!selectedPreset;

        const baseUom = useMemo(() => {
            if (!product?.productuoms || product.productuoms.length === 0) return null;
            return product.productuoms.find((u) => u.factor === 1) || product.productuoms[0] || null;
        }, [product]);

        // Flash sale price: only apply FINAL override if the detail applies to selected variant
        const priceInfo = useMemo(() => {
            if (!matchedVariant) return { priceSell: 0, priceBefore: 0 };
            const basePrice = getProductPrice(product, matchedVariant, pricePolicyId);

            // Only apply flash sale FINAL pricing if detail matches this variant
            if (detailAppliesToVariant && matchingDetail.pricing_mode === PRICING_MODE_FINAL && matchingDetail.uom_unit_price > 0) {
                return {
                    priceSell: matchingDetail.uom_unit_price,
                    priceBefore: basePrice.priceSell,
                };
            }
            return basePrice;
        }, [product, matchedVariant, pricePolicyId, matchingDetail, detailAppliesToVariant]);

        const displayPrice = useMemo(() => {
            if (isKgProduct && selectedPreset && selectedPreset.factor > 0) {
                return priceInfo.priceSell * selectedPreset.factor;
            }
            return isKgProduct ? priceInfo.priceSell * 1000 : priceInfo.priceSell;
        }, [priceInfo, isKgProduct, selectedPreset]);

        const originalPrice = useMemo(() => {
            if (isKgProduct && selectedPreset && selectedPreset.factor > 0) {
                return priceInfo.priceBefore * selectedPreset.factor;
            }
            return isKgProduct ? priceInfo.priceBefore * 1000 : priceInfo.priceBefore;
        }, [priceInfo, isKgProduct, selectedPreset]);

        const discountPercent = useMemo(() => {
            if (originalPrice > 0 && originalPrice > displayPrice) {
                return Math.round((1 - displayPrice / originalPrice) * 100);
            }
            return 0;
        }, [originalPrice, displayPrice]);

        const flashSaleDisplayName = flashSale.title || flashSale.name;
        const flashSaleUrl = `/flashsale/${flashSale.seo_url || flashSale.id}`;

        const [processing, setProcessing] = useState(false);
        const [processingBuyNow, setProcessingBuyNow] = useState(false);
        const [added, setAdded] = useState(false);
        const [errorMsg, setErrorMsg] = useState("");

        const buildPayload = useCallback(() => {
            const presetFactor = (selectedPreset && selectedPreset.factor > 0) ? selectedPreset.factor : 1000;
            const apiPrice = isKgProduct ? displayPrice / presetFactor : displayPrice;
            const apiOriginalPrice = isKgProduct
                ? (originalPrice > 0 ? originalPrice / presetFactor : apiPrice)
                : (originalPrice > 0 ? originalPrice : displayPrice);
            let apiQuantity = quantity;
            if (isKgProduct) {
                if (hasPreset && selectedPreset) {
                    apiQuantity = quantity * selectedPreset.factor;
                } else {
                    apiQuantity = quantity * 1000;
                }
            }
            const uomId = baseUom ? (baseUom.uom_id ?? baseUom.uomid ?? 0) : 0;
            const uomName = baseUom ? (baseUom.uom_name ?? baseUom.uomname ?? "") : "";
            return {
                section: "flashsale",
                product_variant_id: matchedVariant?.id || 0,
                uom_quantity: apiQuantity,
                uom_id: uomId,
                uom_name: uomName,
                weight_sale_mode: product.weightsalemode || 0,
                weight_preset_id: isKgProduct && selectedPreset ? selectedPreset.id : "",
                weight_preset_name: isKgProduct && selectedPreset ? selectedPreset.name : "",
                weight_preset_factor: isKgProduct && selectedPreset ? selectedPreset.factor : 0,
                pricing_type: product.pricingtype || 0,
                calculated_price: apiPrice,
                selected_options: matchedVariant?.optionvalues || [],
                unit_price_original: apiOriginalPrice,
                unit_price: apiPrice,
                promotionengine: [],
                pricing_context_type: "flash_sale",
                pricing_context_id: flashSale.id,
                pricing_context_name: flashSaleDisplayName,
            };
        }, [matchedVariant, baseUom, product, displayPrice, originalPrice, flashSale.id, flashSaleDisplayName, isKgProduct, hasPreset, selectedPreset, quantity]);

        // Build summary text with variant info
        const variantDisplayText = useMemo(() => {
            if (!matchedVariant) return '';
            if (Array.isArray(matchedVariant.optionvalues) && matchedVariant.optionvalues.length > 0) {
                return matchedVariant.optionvalues.map((ov: any) => ov.option_value_name).join(' / ');
            }
            return '';
        }, [matchedVariant]);

        const summaryText = useMemo(() => {
            const parts: string[] = [`${quantity}`];
            if (isKgProduct && hasPreset && selectedPreset) {
                parts.push(`x ${selectedPreset.name}`);
            } else if (isKgProduct) {
                parts.push(`kg`);
            } else if (variantDisplayText) {
                parts.push(`x ${variantDisplayText}`);
            }
            return parts.join(' ');
        }, [quantity, isKgProduct, hasPreset, selectedPreset, variantDisplayText]);

        const debugTitle = useMemo(() => {
            if (!matchedVariant) return 'Chưa chọn variant';
            return `SKU: ${matchedVariant.sku || 'N/A'} | Variant ID: ${matchedVariant.id}`;
        }, [matchedVariant]);

        const addToCart = useCallback(async () => {
            if (!matchedVariant || !product || isDisabled) return;
            setProcessing(true);
            setErrorMsg("");
            try {
                const res = await fetch("/cart/addajax", {
                    method: "POST",
                    body: JSON.stringify(buildPayload()),
                });
                const data = await res.json();
                const apiData = getApiData<{ cart?: any }>(data);
                const apiErrors = getApiErrors(data);
                if (data.success) {
                    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: apiData?.cart } }));
                    document.dispatchEvent(new CustomEvent("cart:product-added", { detail: { product: { name: product.name } } }));
                    setCart(apiData?.cart);
                    setAdded(true);
                    setTimeout(() => setAdded(false), 2000);
                } else {
                    const msgs = apiErrors.filter((e: string) => !e.startsWith("error_"));
                    setErrorMsg(msgs.length > 0 ? msgs.join(". ") : "Không thể thêm vào giỏ hàng");
                }
            } catch (err) {
                setErrorMsg("Lỗi kết nối, vui lòng thử lại");
            } finally {
                setProcessing(false);
            }
        }, [matchedVariant, product, isDisabled, buildPayload, setCart]);

        const buyNow = useCallback(async () => {
            if (!matchedVariant || !product || isDisabled) return;
            setProcessingBuyNow(true);
            setErrorMsg("");
            try {
                const payload = buildPayload();
                const res = await fetch("/cart/addajax", {
                    method: "POST",
                    body: JSON.stringify(payload),
                });
                const data = await res.json();
                const apiData = getApiData<{ cart?: any }>(data);
                const apiErrors = getApiErrors(data);
                if (data.success) {
                    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart: apiData?.cart } }));
                    setCart(apiData?.cart);
                    window.location.href = "/checkout";
                } else {
                    const msgs = apiErrors.filter((e: string) => !e.startsWith("error_"));
                    setErrorMsg(msgs.length > 0 ? msgs.join(". ") : "Không thể mua ngay");
                }
            } catch (err) {
                setErrorMsg("Lỗi kết nối, vui lòng thử lại");
            } finally {
                setProcessingBuyNow(false);
            }
        }, [matchedVariant, product, isDisabled, buildPayload, setCart]);

        const anyProcessing = processing || processingBuyNow;

        return (
            <div className="border border-red-200 rounded-xl bg-red-50/30 p-3 space-y-2">
                {/* Row 1: Status + Name (link) + Countdown */}
                <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium ${isEnded ? "bg-gray-100 text-gray-500" : "bg-red-100 text-red-600"}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${isEnded ? "bg-gray-400" : "bg-red-500 animate-pulse"}`}></span>
                            {isEnded ? "Đã kết thúc" : "⚡ Flash Sale"}
                        </span>
                        <a href={flashSaleUrl} className="text-sm font-semibold text-gray-800 hover:underline truncate" title={flashSaleDisplayName}>
                            {flashSaleDisplayName}
                        </a>
                    </div>
                    {flashSale.date_ended > 0 && !isEnded && (
                        <CountdownTimer targetTimestamp={flashSale.date_ended} label="Còn lại:" />
                    )}
                </div>

                {/* Flash sale price (shown because it differs from product price) */}
                {displayPrice > 0 && displayPrice !== originalPrice && (
                    <div className="flex items-baseline gap-1.5">
                        <span className="text-base font-bold text-red-600">
                            <TextMoney money={displayPrice} />
                            {isKgProduct && selectedPreset && <span className="text-[10px] font-normal text-red-400">/{selectedPreset.name}</span>}
                            {isKgProduct && !selectedPreset && <span className="text-[10px] font-normal text-red-400">/kg</span>}
                        </span>
                        {originalPrice > 0 && originalPrice !== displayPrice && (
                            <span className="text-xs text-gray-400 line-through"><TextMoney money={originalPrice} /></span>
                        )}
                        {discountPercent > 0 && (
                            <span className="text-[10px] font-bold text-white bg-red-500 px-1 py-0.5 rounded">-{discountPercent}%</span>
                        )}
                    </div>
                )}

                {/* Quota */}
                {quotaTotal > 0 && (
                    <QuotaProgressBar used={quotaUsed} total={quotaTotal} isKgProduct={isKgProduct} />
                )}

                {/* Summary: what will be added */}
                {isVariantSelected && !isEnded && !isQuotaExhausted && (
                    <div className="text-xs text-gray-500" title={debugTitle} data-variant-id={matchedVariant?.id || ''}>
                        Mua Flash Sale: <span className="font-medium text-gray-700">{summaryText}</span>
                    </div>
                )}

                {/* Action buttons */}
                {!isVariantSelected && !isEnded && !isQuotaExhausted ? (
                    <div className="text-xs text-orange-600 font-medium">
                        Vui lòng chọn thuộc tính sản phẩm ở trên
                    </div>
                ) : isEnded || isQuotaExhausted ? (
                    <div className="text-xs text-gray-500 font-medium">
                        {isEnded ? "Flash Sale đã kết thúc" : "Đã hết suất mua"}
                    </div>
                ) : (
                    <div className="flex items-center gap-2" data-variant-id={matchedVariant?.id || ''}>
                        <button
                            className={`btn-primary text-sm font-medium !py-1.5 !px-4 !leading-6 ${anyProcessing ? "opacity-50 cursor-not-allowed" : added ? "!bg-green-500" : ""}`}
                            disabled={anyProcessing}
                            onClick={addToCart}
                            title={debugTitle}
                        >
                            {processing ? "..." : added ? "✓ Đã thêm" : "Thêm giỏ hàng"}
                        </button>
                        {allowBuyNow && (
                            <button
                                className={`btn-primary text-sm font-medium !py-1.5 !px-4 !leading-6 ${anyProcessing ? "opacity-50 cursor-not-allowed" : ""}`}
                                disabled={anyProcessing}
                                onClick={buyNow}
                                title={debugTitle}
                            >
                                {processingBuyNow ? "..." : "Mua ngay"}
                            </button>
                        )}
                    </div>
                )}

                {/* Error */}
                {errorMsg && (
                    <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-2.5 py-1.5">
                        {errorMsg}
                    </div>
                )}
            </div>
        );
    };

// ─── Main Component ───

const ProductFlashSaleBlocks: React.FC<{
    flashSales: FlashSaleData[];
    product: ApiProduct;
    pricePolicyId: number;
    allowBuyNow: boolean;
    defaultWeightPresets: any[];
}> = ({ flashSales, product, pricePolicyId, allowBuyNow, defaultWeightPresets }) => {
    const setCart = useCartStore((state) => state.setCart);
    const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
    const [selectedPreset, setSelectedPreset] = useState<any>(null);
    const [quantity, setQuantity] = useState(1);

    // Listen to product:option-changed
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            if (detail?.selectedVariant?.id) {
                setSelectedVariantId(detail.selectedVariant.id);
            } else {
                setSelectedVariantId(null);
            }
        };
        window.addEventListener("product:option-changed", handler);
        return () => window.removeEventListener("product:option-changed", handler);
    }, []);

    // Listen to preset changes
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            setSelectedPreset(detail?.selectedPreset || null);
        };
        window.addEventListener("product:preset-changed", handler);
        return () => window.removeEventListener("product:preset-changed", handler);
    }, []);

    // Listen to quantity changes
    useEffect(() => {
        const handler = (e: any) => {
            const detail = e.detail;
            if (detail?.quantity && detail.quantity > 0) {
                setQuantity(detail.quantity);
            }
        };
        window.addEventListener("product:quantity-changed", handler);
        return () => window.removeEventListener("product:quantity-changed", handler);
    }, []);

    // If no preset from event, auto-select from product or defaults
    const effectivePreset = useMemo(() => {
        if (selectedPreset) return selectedPreset;
        const isKg = product.weightsalemode === Product.WEIGHT_SALE_MODE_WEIGHT || product.weightsalemode === Product.WEIGHT_SALE_MODE_COUNTING;
        if (!isKg) return null;
        const displayPreset = resolveProductDisplayPreset(product);
        if (displayPreset) return displayPreset;
        if (Array.isArray(defaultWeightPresets) && defaultWeightPresets.length > 0) return defaultWeightPresets[0];
        return null;
    }, [selectedPreset, product, defaultWeightPresets]);

    if (!flashSales || flashSales.length === 0) return null;

    return (
        <div className="mt-5 space-y-3">
            {flashSales.map((fs) => (
                <FlashSaleBlock
                    key={fs.id}
                    flashSale={fs}
                    product={product}
                    selectedVariantId={selectedVariantId}
                    selectedPreset={effectivePreset}
                    quantity={quantity}
                    pricePolicyId={pricePolicyId}
                    allowBuyNow={allowBuyNow}
                    setCart={setCart}
                />
            ))}
        </div>
    );
};

export default ProductFlashSaleBlocks;
